import { useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, RefreshCcw, ScrollText, X } from 'lucide-react';
import { DoorlyLogo } from './DoorlyLogo';
import { checkForUpdates } from '../lib/updateCheck';
import { toast } from './Toaster';
import { cn } from '../lib/cn';

/**
 * Version stamp that opens an "À propos" modal on click. The modal surfaces
 * the build version + a manual "check for updates" control so a vendor can
 * confirm they're on the latest bundle without digging into the avatar menu.
 */
export function AppVersion() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="À propos de Doorly"
        className="w-full px-3.5 py-2.5 flex items-center justify-center gap-1.5 text-micro text-ink-mute font-mono hover:bg-surface-alt transition-colors"
      >
        <span className="truncate">Doorly {__APP_VERSION__}</span>
      </button>
      {open && <AboutModal onClose={() => setOpen(false)} />}
    </>
  );
}

function AboutModal({ onClose }: { onClose: () => void }) {
  const [checking, setChecking] = useState(false);

  async function onCheckClick() {
    setChecking(true);
    try {
      const result = await checkForUpdates();
      if (result === 'updating') {
        toast.info('Mise à jour détectée — rechargement…');
      } else if (result === 'no-sw') {
        toast.info('Service worker inactif — vérification dispo seulement en prod.');
      } else if (result === 'unsupported') {
        toast.info('Votre navigateur ne supporte pas la vérification.');
      } else {
        toast.success('Vous êtes à jour ✓');
      }
    } finally {
      setChecking(false);
    }
  }

  // Portal to document.body — escapes any ancestor stacking context (sidebar,
  // recharts ResponsiveContainer, etc.) so the modal always sits on top
  // regardless of where AppVersion is mounted in the tree. Bumping z-index
  // alone wasn't enough on Chrome: recharts SVGs were still bleeding through.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[100] bg-ink/30 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[101] grid place-items-center p-4"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-sm bg-surface border border-hair p-6 flex flex-col items-center gap-4"
          style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-elevated)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="absolute top-3 right-3 p-1.5 rounded-full text-ink-mute hover:bg-surface-alt"
          >
            <X className="h-4 w-4" />
          </button>

          <DoorlyLogo variant="icon" size={56} />
          <div className="text-center">
            <div className="text-title font-extrabold text-ink">Doorly</div>
            <div className="text-meta text-ink-mute font-mono mt-1">v{__APP_VERSION__}</div>
          </div>

          <button
            type="button"
            onClick={() => void onCheckClick()}
            disabled={checking}
            className="w-full mt-2 flex items-center justify-center gap-2 h-10 rounded-[var(--radius-sm)] bg-brand text-brand-ink text-body font-bold hover:brightness-110 disabled:opacity-60"
          >
            <RefreshCcw className={cn('h-4 w-4', checking && 'animate-spin')} />
            {checking ? 'Vérification…' : 'Vérifier les mises à jour'}
          </button>

          <div className="w-full border-t border-hair pt-4 mt-2 grid grid-cols-2 gap-2">
            {/* Plain anchors (not <Link>) so navigation triggers a full reload
                — App.tsx then renders LegalPage standalone, bypassing the
                AppShell layout. */}
            <a
              href="/legal?doc=cgu"
              className="flex items-center justify-center gap-1.5 h-9 rounded-[var(--radius-sm)] border border-hair text-meta font-semibold text-ink-soft hover:bg-surface-alt"
            >
              <ScrollText className="h-3.5 w-3.5" />
              CGU
            </a>
            <a
              href="/legal?doc=mentions"
              className="flex items-center justify-center gap-1.5 h-9 rounded-[var(--radius-sm)] border border-hair text-meta font-semibold text-ink-soft hover:bg-surface-alt"
            >
              <FileText className="h-3.5 w-3.5" />
              Mentions légales
            </a>
          </div>
          <p className="text-micro text-ink-mute text-center">
            © WEEDEV — Tous droits réservés
          </p>
        </div>
      </div>
    </>,
    document.body,
  );
}
